import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/**
 * Maps each dimension field to the CSV header that supplies it. Omitted fields
 * fall back to auto-detection from the uploaded file's headers.
 */
const columnMapping = z.object({
  variantId: z.string().optional(),
  length: z.string().optional(),
  width: z.string().optional(),
  height: z.string().optional(),
  padding: z.string().optional(),
  sku: z.string().optional(),
  productTitle: z.string().optional(),
  variantTitle: z.string().optional(),
});

const metafieldKeys = z.object({
  namespace: z.string().optional(),
  length: z.string().optional(),
  width: z.string().optional(),
  height: z.string().optional(),
  padding: z.string().optional(),
  type: z.string().optional(),
});

const definitionConflict = z.object({
  key: z.string(),
  definitionType: z.string().optional(),
  conflict: z.boolean(),
});

const previewVariant = z.object({
  sku: z.string(),
  variantGid: z.string(),
  length: z.string(),
  width: z.string(),
  height: z.string(),
  /** Empty when the sheet had no padding column. */
  padding: z.string(),
});

export const importVariantDimensionsCsv = {
  name: "importVariantDimensionsCsv" as const,
  input: z.object({
    teamId: zExt.objectId(),
    /** When true, validate and report only — no metafields are written. */
    dryRun: z.boolean(),
    /**
     * Which column identifies the variant. Sheets exported from Shopify carry
     * a numeric variant ID; merchant-authored sheets normally key by SKU.
     */
    keyBy: z.enum(["variantId", "sku"]),
    mapping: columnMapping.optional(),
    keys: metafieldKeys.optional(),
    /** Write even though a conflicting metafield definition was detected. */
    force: z.boolean().optional(),
  }),
  output: z.object({
    shopName: z.string(),
    myshopifyDomain: z.string(),
    /** Every header found in the uploaded file, for the mapping dropdowns. */
    headers: z.array(z.string()),
    resolvedMapping: columnMapping,
    missingColumns: z.array(z.string()),
    rowsParsed: z.number().int().nonnegative(),
    variantsToUpdate: z.number().int().nonnegative(),
    skippedMissingDimensions: z.number().int().nonnegative(),
    skippedInvalidVariantId: z.number().int().nonnegative(),
    skippedMissingKey: z.number().int().nonnegative(),
    /** SKUs the catalog returned no variant for. */
    unmatchedSkus: z.array(z.string()),
    /** SKUs matching several variants; skipped rather than guessed at. */
    ambiguousSkus: z.array(z.string()),
    /** The key the uploaded file actually supports, for the UI to preselect. */
    suggestedKeyBy: z.enum(["variantId", "sku"]),
    /** False when the sheet has no padding column, so only 3 metafields write. */
    includesPadding: z.boolean(),
    warnings: z.array(z.string()),
    definitionConflicts: z.array(definitionConflict),
    overwriteCount: z.number().int().nonnegative(),
    typeConflictCount: z.number().int().nonnegative(),
    typeConflictSamples: z.array(z.string()),
    hasBlockingConflicts: z.boolean(),
    previewVariants: z.array(previewVariant),
    /** False for a dry run, or when validation stopped the write. */
    applied: z.boolean(),
    metafieldsWritten: z.number().int().nonnegative(),
    metafieldsFailed: z.number().int().nonnegative(),
    errors: z.array(z.string()),
  }),
};
